import React, { useEffect, useState } from 'react'
import { useFormikContext } from 'formik'

import { Button } from '@/components/buttons/Button'
import { FormField } from '@/components/forms/FormField'
import { TextInput } from '@/components/forms/TextInput'

import './Five9CustomFieldsEditor.scss'

let nextRowId = 0
const makeRowId = () => {
  nextRowId += 1
  return `five9-custom-field-row-${nextRowId}`
}

/**
 * Five9-only editor for the `custom_fields` config value: a repeatable list of
 * { key, value } rows. `key` is the URL parameter name Five9 posts back;
 * `value` is the Five9 variable expression (e.g. Customer.car_make_and_model).
 *
 * Self-contained: reads/writes the `custom_fields` field through Formik context
 * so it can be injected via the schema's `raw`/`as` escape hatch with no change
 * to the shared AbstractForm renderer. The value shape matches the backend, so
 * nothing serializes it on submit.
 */
export const Five9CustomFieldsEditor = () => {
  const { values, setFieldValue } = useFormikContext()
  const rows = Array.isArray(values.custom_fields) ? values.custom_fields : []

  // Stable React keys per row so removing a middle row doesn't remap inputs.
  // Reconciled to the row count so keys survive the async config load (rows
  // arrive after mount) without regenerating on every edit.
  const [rowIds, setRowIds] = useState(() => rows.map(makeRowId))
  useEffect(() => {
    if (rowIds.length !== rows.length) {
      setRowIds((previous) => rows.map((_, index) => previous[index] || makeRowId()))
    }
  }, [rows.length, rowIds.length])

  const setRow = (index, patch) => {
    setFieldValue(
      'custom_fields',
      rows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row))
    )
  }
  const addRow = () => {
    setRowIds([...rowIds, makeRowId()])
    setFieldValue('custom_fields', [...rows, { key: '', value: '' }])
  }
  const removeRow = (index) => {
    setRowIds(rowIds.filter((_, idIndex) => idIndex !== index))
    setFieldValue(
      'custom_fields',
      rows.filter((_, rowIndex) => rowIndex !== index)
    )
  }

  return (
    <FormField data-testid="five9-custom-fields">
      <label>Custom Fields</label>
      <div className="form-caption">
        Custom Five9 variables Balto should receive. &ldquo;Field Name&rdquo; is the parameter name
        (no spaces &mdash; use underscores); &ldquo;Five9 Variable&rdquo; is the Five9 expression
        (e.g. Customer.car_make_and_model).
      </div>
      {rows.map((row, index) => (
        <div className="five9-custom-field-row" key={rowIds[index]}>
          <TextInput
            small
            className="five9-custom-field-input"
            placeholder="Field Name"
            value={row.key}
            data-testid={`five9-custom-field-key-${index}`}
            onChange={(event) => setRow(index, { key: event.target.value })}
          />
          <TextInput
            small
            className="five9-custom-field-input"
            placeholder="Five9 Variable"
            value={row.value}
            data-testid={`five9-custom-field-value-${index}`}
            onChange={(event) => setRow(index, { value: event.target.value })}
          />
          <Button
            small
            type="button"
            basic
            aria-label="Remove custom field"
            data-testid={`five9-custom-field-remove-${index}`}
            onClick={() => removeRow(index)}
          >
            Remove
          </Button>
        </div>
      ))}
      <Button small type="button" brand onClick={addRow} data-testid="five9-custom-field-add">
        + Add
      </Button>
    </FormField>
  )
}
